"use client";

import { deleteCalendar } from "@/api/calendars.api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { Calendar } from "@terrabase/db";
import { toast } from "sonner";
import { DataTableColumnHeader } from "./calendars.header";

export const columns: ColumnDef<Calendar>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Calendar Name" />
    ),
    cell: ({ row }) => (
      <div className="font-medium">{row.getValue("name")}</div>
    ),
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Calendar ID" />
    ),
    cell: ({ row }) => (
      <div className="font-mono text-xs text-muted-foreground">
        {row.getValue("id")}
      </div>
    ),
    enableSorting: false,
    enableHiding: true,
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const calendar = row.original;
      const navigate = useNavigate();
      const queryClient = useQueryClient();

      const { mutate: deleteCalendarMutation } = useMutation({
        mutationFn: deleteCalendar,
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["calendars"] });
          toast.success("Calendar deleted successfully");
        },
        onError: (error) => {
          toast.error(error.message || "Failed to delete calendar");
        },
      });

      const handleViewEvents = () => {
        navigate({ to: `/calendar/${calendar.id}/events` });
      };

      const handleEditCalendar = () => {
        // For now, show an alert since we don't have an edit route yet
        alert("Edit calendar functionality coming soon!");
      };

      const handleDeleteCalendar = () => {
        if (
          confirm(
            `Are you sure you want to delete "${calendar.name}"? This action cannot be undone.`,
          )
        ) {
          deleteCalendarMutation(calendar.id);
        }
      };

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <DotsHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(calendar.id)}
            >
              Copy calendar ID
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleViewEvents}>
              View events
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleEditCalendar}>
              Edit calendar
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={handleDeleteCalendar}
            >
              Delete calendar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
